// Image Optimization Utilities

/**
 * Preloads critical images for better performance
 * @param imagePaths Array of image paths to preload
 */
export const preloadCriticalImages = (imagePaths: string[]) => {
  if (!Array.isArray(imagePaths) || imagePaths.length === 0) return;
  
  // Check if we're in the browser environment
  if (typeof window === 'undefined' || typeof document === 'undefined') return;
  
  // Create a Set to avoid duplicate preloads
  const alreadyPreloaded = new Set<string>();
  
  // Get existing preloads to avoid duplication
  document.querySelectorAll('link[rel="preload"][as="image"]').forEach(link => {
    alreadyPreloaded.add((link as HTMLLinkElement).href);
  });
  
  imagePaths.forEach(path => {
    if (typeof path === 'string') {
      const fullUrl = validateImageUrl(path);
      
      // Skip if already preloaded
      if (alreadyPreloaded.has(fullUrl)) return;
      
      const link = document.createElement('link');
      link.rel = 'preload';
      link.as = 'image';
      link.href = fullUrl;
      link.crossOrigin = 'anonymous'; // For CORS compliance
      document.head.appendChild(link);
      
      // Mark as preloaded
      alreadyPreloaded.add(fullUrl);
      
      // Log for debugging in development
      if (process.env.NODE_ENV === 'development') {
        console.log(`Preloaded image: ${fullUrl}`);
      }
    }
  });
};

/**
 * Checks if an image exists and returns a fallback if it doesn't
 * @param imagePath The path to the image to check
 * @param fallbackPath Optional fallback image path
 * @returns The original path or the fallback path
 */
export const getImageWithFallback = (imagePath: string, fallbackPath: string = '/placeholder.svg') => {
  return validateImageUrl(imagePath) || fallbackPath;
};

/**
 * Lazy loads images that are not in the viewport
 * @param selector The CSS selector for images to lazy load
 */
export const setupLazyLoading = (selector: string = 'img[data-lazy="true"]') => {
  if ('IntersectionObserver' in window) {
    const lazyImageObserver = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          const lazyImage = entry.target as HTMLImageElement;
          if (lazyImage.dataset.src) {
            // Create a new image to preload
            const img = new Image();
            img.onload = () => {
              lazyImage.src = lazyImage.dataset.src || '';
              lazyImage.removeAttribute('data-src');
              lazyImage.classList.remove('lazy');
              lazyImage.classList.add('loaded');
            };
            img.src = lazyImage.dataset.src;
            lazyImageObserver.unobserve(lazyImage);
          }
        }
      });
    }, {
      rootMargin: '200px 0px', // Start loading 200px before they come into viewport
      threshold: 0.01
    });

    const lazyImages = document.querySelectorAll(selector);
    lazyImages.forEach(image => {
      lazyImageObserver.observe(image);
    });
  } else {
    // Fallback for browsers that don't support IntersectionObserver
    const lazyImages = document.querySelectorAll(selector);
    lazyImages.forEach(image => {
      const lazyImage = image as HTMLImageElement;
      if (lazyImage.dataset.src) {
        lazyImage.src = lazyImage.dataset.src;
        lazyImage.removeAttribute('data-src');
      }
    });
  }
};

/**
 * Initializes image optimizations for the application
 * This function is called when the application loads
 */
export const initializeOptimizations = () => {
  // Using requestIdleCallback to not block the main thread
  const runOptimizations = () => {
    // Preload hero images on homepage
    const criticalImages = [
      '/lovable-uploads/645ce282-0307-4a13-a3a7-eb036180d6af.png',
      '/lovable-uploads/c3099eea-1cdc-462a-9e3c-85f757436352.png',
      '/lovable-uploads/5d002198-ecfc-4ca4-9b1d-57761c26abb4.png'
    ];
    preloadCriticalImages(criticalImages);
    
    // Setup lazy loading for non-critical images
    setupLazyLoading();
    
    // Prefetch anticipated next-page resources
    prefetchNextPageResources();
    
    // Apply responsive image optimizations
    applyResponsiveImageOptimizations();
    
    if (process.env.NODE_ENV === 'development') {
      console.log('Image optimizations initialized');
    }
  };
  
  // Use requestIdleCallback if available, otherwise use setTimeout
  if ('requestIdleCallback' in window) {
    // @ts-ignore - TypeScript may not have requestIdleCallback types in older versions
    window.requestIdleCallback(runOptimizations);
  } else {
    setTimeout(runOptimizations, 200);
  }
};

/**
 * Applies responsive image optimizations
 */
const applyResponsiveImageOptimizations = () => {
  // Find all images without width/height
  const images = document.querySelectorAll('img:not([width]):not([height])');
  
  images.forEach(img => {
    // Add default dimensions to prevent layout shifts
    if (!img.getAttribute('width') && !img.getAttribute('height')) {
      img.setAttribute('width', '100%');
      img.setAttribute('height', 'auto');
    }
  });
};

/**
 * Prefetches resources for pages the user is likely to visit next
 */
const prefetchNextPageResources = () => {
  // Determine current page
  const currentPath = window.location.pathname;
  
  // Define prefetch targets based on current location
  let prefetchTargets: string[] = [];
  
  if (currentPath === '/' || currentPath === '/index.html') {
    // From homepage, prefetch undervisning and om pages
    prefetchTargets = ['/undervisning', '/om'];
  } else if (currentPath === '/undervisning') {
    // From services page, prefetch contact page
    prefetchTargets = ['/kontakt'];
  } else if (currentPath === '/om') {
    // From about page, prefetch contact page
    prefetchTargets = ['/kontakt'];
  } else if (currentPath.includes('/klaverundervisning-')) {
    // From a location page, prefetch contact and services pages
    prefetchTargets = ['/kontakt', '/undervisning'];
  }
  
  // Create prefetch links
  prefetchTargets.forEach(path => {
    // Skip if already prefetched
    if (document.querySelector(`link[rel="prefetch"][href="${path}"]`)) return;
    
    const link = document.createElement('link');
    link.rel = 'prefetch';
    link.href = path;
    document.head.appendChild(link);
  });
};

/**
 * Helper function to get a valid image URL or fallback to placeholder
 * @param imageUrl The image URL to validate
 * @returns A valid image URL or placeholder
 */
export const validateImageUrl = (imageUrl: string): string => {
  if (!imageUrl) return '/placeholder.svg';
  
  // If the image is a full URL (http/https), return it as is
  if (imageUrl.startsWith('http://') || imageUrl.startsWith('https://')) {
    return imageUrl;
  }
  
  // If the image already starts with /lovable-uploads, keep it as is
  if (imageUrl.startsWith('/lovable-uploads/')) {
    return imageUrl;
  }
  
  // If the image is already in lovable-uploads format but missing the path prefix
  if (imageUrl.match(/^[a-f0-9]{8}-[a-f0-9]{4}-[a-f0-9]{4}-[a-f0-9]{4}-[a-f0-9]{12}\.(png|jpg|jpeg|gif|webp|svg)$/i)) {
    return `/lovable-uploads/${imageUrl}`;
  }
  
  // If it's a relative path that doesn't start with /, add the / prefix
  if (!imageUrl.startsWith('/')) {
    return `/${imageUrl}`;
  }
  
  // Return the unchanged URL for other cases
  return imageUrl;
};

/**
 * Gets the appropriate image size based on viewport width
 * @param defaultSize The default size to use
 * @returns The appropriate image size for the current viewport
 */
export const getResponsiveImageSize = (defaultSize: number = 800): number => {
  if (typeof window === 'undefined') return defaultSize;
  
  const viewportWidth = window.innerWidth;
  
  if (viewportWidth <= 640) {
    return 480; // Mobile
  } else if (viewportWidth <= 1024) {
    return 768; // Tablet
  } else if (viewportWidth <= 1536) {
    return 1024; // Desktop
  } else {
    return 1600; // Large desktop
  }
};
